// Admin Social Media — AI-driven cross-platform composer for TKOS's own
// channels (Instagram, Facebook, LinkedIn). This is the *admin-level* tool
// distinct from per-retailer social-ai at /apps/retailer/src/pages/social-ai.
//
// Pattern: mirror of the retailer's social-ai page — same per-platform fan-out
// of POST /api/social/generate (modules/social.js:112) with a graceful local
// stub fallback when the endpoint isn't available.
//
// Source-of-truth admin standalone has a placeholder for this page
// (admin-standalone.html:1439: "Open the Social Hub from a retailer …"). This
// extraction promotes it to a real composer because TKOS itself needs to post
// product updates, customer stories, industry news, behind-the-scenes content.
//
// "Post now" is a stub here too — admin OAuth credentials for TKOS's own
// social accounts are out of scope.

import { ApiError } from "@tkos/api-client";

import { registerPage } from "../../shell/router";
import type { Page, PageContext, PageCleanup } from "../../shell/types";

import "./styles.css";

// ── Types ────────────────────────────────────────────────────────────────────

const PLATFORMS = ["instagram", "facebook", "linkedin"] as const;
type Platform = (typeof PLATFORMS)[number];

const PLATFORM_META: Record<Platform, { label: string; emoji: string; maxChars: number }> = {
  instagram: { label: "Instagram", emoji: "📸", maxChars: 2200 },
  facebook:  { label: "Facebook",  emoji: "👍", maxChars: 5000 },
  linkedin:  { label: "LinkedIn",  emoji: "💼", maxChars: 3000 },
};

const POST_TYPES = [
  { id: "product-update",   label: "🆕 Product update",
    seed: "A new feature on The Kitchen OS that helps retailers run their showrooms more efficiently." },
  { id: "customer-story",   label: "🌟 Customer story",
    seed: "A retailer success story from The Kitchen OS — how the platform helped them grow." },
  { id: "industry-news",    label: "📰 Industry news",
    seed: "An interesting development in the UK kitchen industry that retailers should know about." },
  { id: "behind-scenes",    label: "🎬 Behind the scenes",
    seed: "A look behind the scenes at how we build The Kitchen OS for kitchen retailers." },
] as const;

type PostTypeId = (typeof POST_TYPES)[number]["id"];

interface ResultEntry {
  platform: Platform;
  text: string;
  loading: boolean;
  error: string | null;
  editing: boolean;
  stubbed: boolean;
}

interface PostedItem {
  id: string;
  platform: Platform;
  text: string;
  posted_at: string;
  engagement?: number;
  clicks?: number;
}

interface GenerateResponse {
  ok?: boolean;
  platform?: string;
  text?: string;
}

interface RecentResponse {
  ok?: boolean;
  posts?: PostedItem[];
}

// ── Constants ────────────────────────────────────────────────────────────────

const MAX_FILE_BYTES = 5 * 1024 * 1024;
const ACCEPTED_MIME = ["image/jpeg", "image/png", "image/webp"];
const ACCEPTED_EXT = /\.(jpe?g|png|webp)$/i;

// ── Helpers ──────────────────────────────────────────────────────────────────

function esc(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

function isAbortError(err: unknown): boolean {
  return err instanceof DOMException && err.name === "AbortError";
}

function isNotFound(err: unknown): boolean {
  return err instanceof ApiError && err.status === 404;
}

function formatBytes(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes <= 0) return "—";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function stripDataUrl(dataUrl: string): string {
  const comma = dataUrl.indexOf(",");
  return comma >= 0 ? dataUrl.slice(comma + 1) : dataUrl;
}

function fmtDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, {
    month: "short", day: "numeric",
    hour: "2-digit", minute: "2-digit",
  });
}

function hash(s: string): number {
  let h = 0;
  for (let i = 0; i < s.length; i++) {
    h = (h * 31 + s.charCodeAt(i)) | 0;
  }
  return Math.abs(h);
}

// ── Stub generator (same envelope as retailer/social-ai) ─────────────────────

const STUB_HASHTAGS_IG = [
  "#TheKitchenOS", "#KitchenTech", "#KitchenIndustry", "#SmallBusinessTech",
  "#UKKitchens", "#KitchenRetail", "#ShowroomSoftware",
];
const STUB_HASHTAGS_LI = [
  "#SaaS", "#RetailTech", "#KitchenIndustry", "#FoundedInTheUK",
  "#PlatformBusiness", "#CustomerExperience",
];

function pickRotating<T>(arr: readonly T[], count: number, seed: number): T[] {
  const out: T[] = [];
  for (let i = 0; i < count && arr.length > 0; i++) {
    const item = arr[(seed + i) % arr.length] as T;
    out.push(item);
  }
  return out;
}

function stubGenerate(platform: Platform, brief: string, hasImage: boolean): string {
  const seed = hash(brief + platform);
  const trimmed = brief.trim() || "Something we shipped this week at The Kitchen OS";
  const imageNote = hasImage ? " (see image) " : " ";

  if (platform === "instagram") {
    const tags = pickRotating(STUB_HASHTAGS_IG, 5, seed).join(" ");
    return `${trimmed}${imageNote}🚀\n\nFollow us for more on how kitchen retailers are running their showrooms in 2026.\n\n${tags}`;
  }
  if (platform === "facebook") {
    return (
      `${trimmed}.${imageNote}This is exactly the kind of thing our retailers asked us to build — and we shipped it.\n\n` +
      `If you run a kitchen showroom in the UK and you want to see what The Kitchen OS can do for you, drop us a message or visit thekitchenos.io.`
    );
  }
  // linkedin
  return (
    `Quick thought from the team at The Kitchen OS — ${trimmed.toLowerCase()}.${imageNote}\n\n` +
    `Building software for an industry like this means listening hard, shipping small, and being honest about what's still rough. ` +
    `We're proud of where the platform is — and clear on where it's going next.\n\n` +
    `${pickRotating(STUB_HASHTAGS_LI, 3, seed).join(" ")}`
  );
}

// ── Stub recent posts (when /admin/social/recent isn't wired) ────────────────

const STUB_RECENT: PostedItem[] = [
  { id: "p1", platform: "instagram", text: "We just shipped Quote Builder v3 — drag-and-drop kitchen layout straight from the showroom floor. 🎯 #TheKitchenOS #KitchenTech", posted_at: new Date(Date.now() - 86_400_000 * 1).toISOString(), engagement: 138, clicks: 22 },
  { id: "p2", platform: "linkedin",  text: "Our retailers report 27% faster quote turnaround since switching to The Kitchen OS. The boring stuff — quote speed, payment certainty, supplier visibility — is the work that compounds.", posted_at: new Date(Date.now() - 86_400_000 * 3).toISOString(), engagement: 84, clicks: 41 },
  { id: "p3", platform: "facebook",  text: "Behind the scenes at TKOS HQ this week — onboarding two new wholesale partners to the supplier catalogue. More kitchens, more choice, same platform.", posted_at: new Date(Date.now() - 86_400_000 * 6).toISOString(), engagement: 52, clicks: 8 },
];

// ── Page ─────────────────────────────────────────────────────────────────────

const socialPage: Page = {
  name: "social",
  label: "Social Media",
  icon: "📱",

  mount(container: HTMLElement, ctx: PageContext): PageCleanup {
    const ac = new AbortController();

    // ── State ──────────────────────────────────────────────────────────────
    let selectedPlatforms: Set<Platform> = new Set(["instagram", "facebook", "linkedin"]);
    let description = "";
    let postType: PostTypeId | null = null;
    let imageDataUrl: string | null = null;
    let imageName = "";
    let imageSize = 0;
    let imageObjectUrl: string | null = null;
    let results: ResultEntry[] = [];
    let isGenerating = false;
    let stubModeSticky = false;
    let recent: PostedItem[] | null = null;
    let recentStubbed = false;

    // ── DOM scaffold ──────────────────────────────────────────────────────
    const root = document.createElement("section");
    root.className = "asoc-page";
    root.innerHTML = `
      <header class="asoc-header">
        <div>
          <h1>Social Media</h1>
          <p>TKOS's own channels — one brief, three platform-tailored posts. Mirror of the retailer composer with admin-level voice.</p>
        </div>
        <span class="asoc-badge" aria-hidden="true">AI</span>
      </header>

      <div class="asoc-banner" data-role="stub-banner" hidden>
        <strong>Live AI offline</strong> — showing example copy until <code>/api/social/generate</code> is reachable.
      </div>

      <section class="asoc-card">
        <div>
          <p class="asoc-label">Post to</p>
          <div class="asoc-platforms" data-role="platforms" role="group" aria-label="Select platforms">
            ${PLATFORMS.map((p) => `
              <button
                type="button"
                class="asoc-chip"
                data-plat="${p}"
                data-active="${selectedPlatforms.has(p) ? "true" : "false"}"
                aria-pressed="${selectedPlatforms.has(p) ? "true" : "false"}"
              >${PLATFORM_META[p].emoji} ${esc(PLATFORM_META[p].label)}</button>
            `).join("")}
          </div>
        </div>

        <div>
          <p class="asoc-label">Image <span class="asoc-hint">(optional, JPG/PNG/WebP, max 5 MB)</span></p>
          <div class="asoc-drop" data-role="drop" data-has-image="false" data-dragging="false" role="button" tabindex="0" aria-label="Upload image">
            <div class="asoc-drop__placeholder">
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <rect x="3" y="3" width="18" height="18" rx="3"/>
                <circle cx="8.5" cy="8.5" r="1.5"/>
                <polyline points="21 15 16 10 5 21"/>
              </svg>
              <span>Drop an image here or <em>click to upload</em> — AI will reference it in the post</span>
            </div>
            <div class="asoc-drop__preview">
              <img data-role="preview-img" src="" alt="Selected image preview" />
              <div class="asoc-drop__info">
                <span class="asoc-drop__name" data-role="preview-name"></span>
                <span class="asoc-drop__meta" data-role="preview-meta"></span>
              </div>
              <button type="button" class="asoc-drop__remove" data-role="image-remove">✕ Remove</button>
            </div>
          </div>
          <input type="file" data-role="file-input" accept="image/jpeg,image/png,image/webp,.jpg,.jpeg,.png,.webp" hidden />
        </div>

        <div>
          <p class="asoc-label">Description</p>
          <textarea
            class="asoc-textarea"
            data-role="description"
            rows="5"
            placeholder="What's the post about? — e.g. 'New Stripe payouts feature ships today. Retailers paid out next-day instead of weekly.'"
          ></textarea>
        </div>

        <div>
          <p class="asoc-label">Quick types</p>
          <div class="asoc-quick" data-role="quick-types">
            ${POST_TYPES.map((t) => `
              <button type="button" class="asoc-quick__btn" data-quick="${t.id}">${esc(t.label)}</button>
            `).join("")}
          </div>
        </div>

        <div class="asoc-actions">
          <button type="button" class="adm-btn" data-role="generate">Generate</button>
        </div>
      </section>

      <div class="asoc-results" data-role="results" aria-live="polite"></div>

      <section class="asoc-recent" data-role="recent">
        <h2>Recent posts &amp; engagement</h2>
        <div class="adm-loading" data-role="recent-body">Loading…</div>
      </section>
    `;
    container.replaceChildren(root);

    // ── DOM refs ──────────────────────────────────────────────────────────
    const stubBanner    = root.querySelector<HTMLDivElement>("[data-role='stub-banner']")!;
    const platformsEl   = root.querySelector<HTMLDivElement>("[data-role='platforms']")!;
    const dropEl        = root.querySelector<HTMLDivElement>("[data-role='drop']")!;
    const fileInput     = root.querySelector<HTMLInputElement>("[data-role='file-input']")!;
    const previewImg    = root.querySelector<HTMLImageElement>("[data-role='preview-img']")!;
    const previewName   = root.querySelector<HTMLSpanElement>("[data-role='preview-name']")!;
    const previewMeta   = root.querySelector<HTMLSpanElement>("[data-role='preview-meta']")!;
    const removeBtn     = root.querySelector<HTMLButtonElement>("[data-role='image-remove']")!;
    const descEl        = root.querySelector<HTMLTextAreaElement>("[data-role='description']")!;
    const quickEl       = root.querySelector<HTMLDivElement>("[data-role='quick-types']")!;
    const generateBtn   = root.querySelector<HTMLButtonElement>("[data-role='generate']")!;
    const resultsEl     = root.querySelector<HTMLDivElement>("[data-role='results']")!;
    const recentBody    = root.querySelector<HTMLDivElement>("[data-role='recent-body']")!;

    // ── Render helpers ────────────────────────────────────────────────────

    function renderPlatformChips(): void {
      for (const c of Array.from(platformsEl.querySelectorAll<HTMLButtonElement>(".asoc-chip"))) {
        const p = c.dataset["plat"] as Platform | undefined;
        if (!p) continue;
        const active = selectedPlatforms.has(p);
        c.dataset["active"] = active ? "true" : "false";
        c.setAttribute("aria-pressed", active ? "true" : "false");
      }
    }

    function renderImagePreview(): void {
      if (imageDataUrl) {
        dropEl.dataset["hasImage"] = "true";
        previewImg.src = imageObjectUrl ?? imageDataUrl;
        previewName.textContent = imageName || "Image";
        previewMeta.textContent = formatBytes(imageSize);
      } else {
        dropEl.dataset["hasImage"] = "false";
        previewImg.removeAttribute("src");
        previewName.textContent = "";
        previewMeta.textContent = "";
      }
    }

    function renderResults(): void {
      if (results.length === 0) {
        resultsEl.innerHTML = "";
        return;
      }
      resultsEl.innerHTML = results.map(renderResultPanel).join("");
    }

    function renderResultPanel(r: ResultEntry): string {
      const meta = PLATFORM_META[r.platform];
      const count = r.text.length;
      const over = count > meta.maxChars;
      const countLabel = `${count} / ${meta.maxChars}${over ? " — over limit" : ""}`;

      let body: string;
      if (r.loading) {
        body = `<div class="asoc-result__loading">Generating ${esc(meta.label)} copy…</div>`;
      } else if (r.error) {
        body = `<div class="asoc-result__error">${esc(r.error)}</div>`;
      } else if (r.editing) {
        body = `<textarea class="asoc-result__edit" data-role="edit-area" data-plat="${r.platform}">${esc(r.text)}</textarea>`;
      } else {
        body = `<div class="asoc-result__body">${esc(r.text)}</div>`;
      }

      const actions = r.loading ? "" : `
        <div class="asoc-result__actions">
          ${r.editing
            ? `<button type="button" class="adm-btn adm-btn-ghost" data-action="save-edit" data-plat="${r.platform}">Save</button>
               <button type="button" class="adm-btn adm-btn-ghost" data-action="cancel-edit" data-plat="${r.platform}">Cancel</button>`
            : `<button type="button" class="adm-btn adm-btn-ghost" data-action="edit" data-plat="${r.platform}">✎ Edit</button>
               <button type="button" class="adm-btn adm-btn-ghost" data-action="copy" data-plat="${r.platform}">📋 Copy</button>
               <button type="button" class="adm-btn adm-btn-ghost" data-action="regen" data-plat="${r.platform}">↻ Regenerate</button>
               <button type="button" class="adm-btn adm-btn-ghost" data-action="post" data-plat="${r.platform}">${esc(meta.emoji)} Post now</button>`
          }
        </div>
      `;

      return `
        <article class="asoc-result" data-plat="${r.platform}">
          <header class="asoc-result__head">
            <span class="asoc-result__platform">${esc(meta.emoji)} ${esc(meta.label)}${r.stubbed ? " · example" : ""}</span>
            <span class="asoc-result__count">${esc(countLabel)}</span>
          </header>
          ${body}
          ${actions}
        </article>
      `;
    }

    function renderRecent(): void {
      if (!recent) {
        recentBody.innerHTML = `<div class="adm-loading">Loading…</div>`;
        return;
      }
      const banner = recentStubbed
        ? `<div class="asoc-banner asoc-banner--inline"><code>/api/admin/social/recent</code> not wired — showing example data.</div>`
        : "";
      if (recent.length === 0) {
        recentBody.innerHTML = `${banner}<div class="adm-empty">No posts yet.</div>`;
        return;
      }
      const rows = recent.map((p) => `
        <article class="asoc-recent__item" data-plat="${p.platform}">
          <header class="asoc-recent__head">
            <span class="asoc-recent__platform">${esc(PLATFORM_META[p.platform].emoji)} ${esc(PLATFORM_META[p.platform].label)}</span>
            <span class="asoc-recent__when">${esc(fmtDate(p.posted_at))}</span>
          </header>
          <p class="asoc-recent__text">${esc(p.text)}</p>
          <footer class="asoc-recent__stats">
            <span><strong>${p.engagement ?? 0}</strong> engagement</span>
            <span><strong>${p.clicks ?? 0}</strong> clicks</span>
          </footer>
        </article>
      `).join("");
      recentBody.innerHTML = `${banner}<div class="asoc-recent__grid">${rows}</div>`;
    }

    function setGenerating(v: boolean): void {
      isGenerating = v;
      generateBtn.disabled = v;
      generateBtn.textContent = v ? "Generating…" : "Generate";
    }

    function setBannerVisible(v: boolean): void {
      stubBanner.hidden = !v;
    }

    // ── Image handling ────────────────────────────────────────────────────

    function clearImage(): void {
      imageDataUrl = null;
      imageName = "";
      imageSize = 0;
      if (imageObjectUrl) {
        URL.revokeObjectURL(imageObjectUrl);
        imageObjectUrl = null;
      }
      fileInput.value = "";
      renderImagePreview();
    }

    function acceptFile(file: File): void {
      const okMime = ACCEPTED_MIME.includes(file.type);
      const okExt = ACCEPTED_EXT.test(file.name);
      if (!okMime && !okExt) {
        ctx.showToast("Image must be JPG, PNG, or WebP", "error");
        return;
      }
      if (file.size > MAX_FILE_BYTES) {
        ctx.showToast(`Image too large — max ${formatBytes(MAX_FILE_BYTES)}`, "error");
        return;
      }

      if (imageObjectUrl) URL.revokeObjectURL(imageObjectUrl);
      imageObjectUrl = URL.createObjectURL(file);
      imageName = file.name;
      imageSize = file.size;

      const reader = new FileReader();
      reader.onload = (): void => {
        const result = reader.result;
        imageDataUrl = typeof result === "string" ? result : null;
        renderImagePreview();
      };
      reader.onerror = (): void => {
        ctx.showToast("Could not read image", "error");
        clearImage();
      };
      reader.readAsDataURL(file);
      renderImagePreview();
    }

    // ── Backend ───────────────────────────────────────────────────────────

    function buildBrief(): string {
      const d = description.trim();
      if (d) return d;
      if (postType) {
        const spec = POST_TYPES.find((t) => t.id === postType);
        if (spec) return spec.seed;
      }
      return "Write a compelling social-media post about The Kitchen OS for kitchen retailers.";
    }

    async function callBackend(platform: Platform, brief: string): Promise<string> {
      const extra = imageDataUrl
        ? `An image is attached (filename: ${imageName}); reference it naturally as if describing it.`
        : `This post is for The Kitchen OS (TKOS), a SaaS platform for UK kitchen retailers — voice: confident, founder-led, not corporate.`;

      const resp = await ctx.api<GenerateResponse>("/social/generate", {
        method: "POST",
        signal: ac.signal,
        body: JSON.stringify({
          platform,
          brief,
          extra,
          ...(imageDataUrl ? { imageBase64: stripDataUrl(imageDataUrl) } : {}),
        }),
      });
      const text = (resp?.text ?? "").trim();
      if (!text) throw new Error("Empty response from AI");
      return text;
    }

    async function generateForPlatform(platform: Platform, brief: string): Promise<{ text: string; stubbed: boolean }> {
      if (stubModeSticky) {
        return { text: stubGenerate(platform, brief, !!imageDataUrl), stubbed: true };
      }
      try {
        const text = await callBackend(platform, brief);
        return { text, stubbed: false };
      } catch (err) {
        if (isAbortError(err)) throw err;
        if (isNotFound(err)) {
          if (!stubModeSticky) {
            stubModeSticky = true;
            setBannerVisible(true);
            console.info("[admin/social] /api/social/generate not available; using local stub generator.");
          }
          return { text: stubGenerate(platform, brief, !!imageDataUrl), stubbed: true };
        }
        throw err;
      }
    }

    async function runGenerate(): Promise<void> {
      if (isGenerating) return;
      const platforms = Array.from(selectedPlatforms);
      if (platforms.length === 0) {
        ctx.showToast("Pick at least one platform", "error");
        return;
      }
      const brief = buildBrief();
      if (!brief.trim() && !imageDataUrl) {
        ctx.showToast("Add a description or pick a quick type", "error");
        descEl.focus();
        return;
      }

      results = PLATFORMS.filter((p) => selectedPlatforms.has(p)).map((platform) => ({
        platform,
        text: "",
        loading: true,
        error: null,
        editing: false,
        stubbed: false,
      }));
      renderResults();
      setGenerating(true);

      const settled = await Promise.allSettled(
        platforms.map(async (p) => {
          const out = await generateForPlatform(p, brief);
          return { platform: p, ...out };
        }),
      );

      if (ac.signal.aborted) return;

      let anyStub = false;
      let anyError = false;
      results = results.map((r) => {
        const fulfilled = settled.find((s) => s.status === "fulfilled" && s.value.platform === r.platform);
        if (fulfilled && fulfilled.status === "fulfilled") {
          if (fulfilled.value.stubbed) anyStub = true;
          return { ...r, loading: false, text: fulfilled.value.text, stubbed: fulfilled.value.stubbed };
        }
        return r;
      });

      settled.forEach((s, i) => {
        if (s.status !== "rejected") return;
        if (isAbortError(s.reason)) return;
        anyError = true;
        const platform = platforms[i];
        if (!platform) return;
        const idx = results.findIndex((r) => r.platform === platform);
        if (idx < 0) return;
        const cur = results[idx];
        if (!cur) return;
        const message = s.reason instanceof Error ? s.reason.message : "Generation failed";
        results[idx] = { ...cur, loading: false, error: message };
      });

      setGenerating(false);
      renderResults();

      if (anyError) {
        ctx.showToast("Some platforms failed — see panels", "error");
      } else if (anyStub) {
        ctx.showToast("Showing example copy (live AI offline)", "info");
      } else {
        ctx.showToast("Posts generated", "success");
      }
    }

    async function regenerateOne(platform: Platform): Promise<void> {
      const idx = results.findIndex((r) => r.platform === platform);
      if (idx < 0) return;
      const cur = results[idx];
      if (!cur) return;
      results[idx] = { ...cur, loading: true, error: null, editing: false };
      renderResults();
      try {
        const out = await generateForPlatform(platform, buildBrief());
        if (ac.signal.aborted) return;
        const next = results.findIndex((r) => r.platform === platform);
        if (next >= 0) {
          const target = results[next];
          if (!target) return;
          results[next] = { ...target, loading: false, text: out.text, stubbed: out.stubbed };
        }
        renderResults();
        ctx.showToast(`${PLATFORM_META[platform].label} regenerated`, "success");
      } catch (err) {
        if (isAbortError(err)) return;
        const next = results.findIndex((r) => r.platform === platform);
        if (next >= 0) {
          const target = results[next];
          if (target) {
            results[next] = {
              ...target,
              loading: false,
              error: err instanceof Error ? err.message : "Regeneration failed",
            };
          }
        }
        renderResults();
        ctx.showToast("Could not regenerate", "error");
      }
    }

    async function copyToClipboard(text: string): Promise<void> {
      try {
        await navigator.clipboard?.writeText(text);
        ctx.showToast("Copied to clipboard", "success");
      } catch {
        ctx.showToast("Could not copy — select manually", "error");
      }
    }

    async function fetchRecent(): Promise<void> {
      try {
        const resp = await ctx.api<RecentResponse>("/admin/social/recent", {
          method: "GET",
          signal: ac.signal,
        });
        recent = Array.isArray(resp.posts) ? resp.posts : [];
        recentStubbed = false;
      } catch (err) {
        if (isAbortError(err)) return;
        if (isNotFound(err)) {
          recent = [...STUB_RECENT];
          recentStubbed = true;
          return;
        }
        recent = [];
        console.warn("[admin/social] /admin/social/recent failed:", err);
      } finally {
        renderRecent();
      }
    }

    // ── Event handlers ────────────────────────────────────────────────────

    const onPlatformClick = (e: MouseEvent): void => {
      const btn = (e.target as HTMLElement | null)?.closest<HTMLButtonElement>(".asoc-chip");
      if (!btn) return;
      const p = btn.dataset["plat"] as Platform | undefined;
      if (!p || !PLATFORMS.includes(p)) return;
      if (selectedPlatforms.has(p)) {
        if (selectedPlatforms.size === 1) {
          ctx.showToast("Pick at least one platform", "info");
          return;
        }
        selectedPlatforms.delete(p);
      } else {
        selectedPlatforms.add(p);
      }
      renderPlatformChips();
    };

    const onDropClick = (): void => fileInput.click();
    const onDropKey = (e: KeyboardEvent): void => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        fileInput.click();
      }
    };
    const onDragOver = (e: DragEvent): void => {
      e.preventDefault();
      dropEl.dataset["dragging"] = "true";
    };
    const onDragLeave = (e: DragEvent): void => {
      e.preventDefault();
      dropEl.dataset["dragging"] = "false";
    };
    const onDrop = (e: DragEvent): void => {
      e.preventDefault();
      dropEl.dataset["dragging"] = "false";
      const file = e.dataTransfer?.files?.[0];
      if (file) acceptFile(file);
    };
    const onFileChange = (): void => {
      const file = fileInput.files?.[0];
      if (file) acceptFile(file);
    };
    const onRemoveImage = (e: MouseEvent): void => {
      e.stopPropagation();
      clearImage();
    };
    const onDescInput = (): void => {
      description = descEl.value;
    };
    const onQuickClick = (e: MouseEvent): void => {
      const btn = (e.target as HTMLElement | null)?.closest<HTMLButtonElement>(".asoc-quick__btn");
      if (!btn) return;
      const id = btn.dataset["quick"] as PostTypeId | undefined;
      const spec = POST_TYPES.find((t) => t.id === id);
      if (!spec) return;
      postType = spec.id;
      if (description.trim()) {
        description = `${description.trim()}\n\n${spec.seed}`;
      } else {
        description = spec.seed;
      }
      descEl.value = description;
      descEl.focus();
    };
    const onGenerateClick = (): void => {
      void runGenerate();
    };
    const onResultsClick = (e: MouseEvent): void => {
      const btn = (e.target as HTMLElement | null)?.closest<HTMLButtonElement>("button[data-action]");
      if (!btn) return;
      const action = btn.dataset["action"];
      const platform = btn.dataset["plat"] as Platform | undefined;
      if (!platform) return;
      const idx = results.findIndex((r) => r.platform === platform);
      if (idx < 0) return;
      const cur = results[idx];
      if (!cur) return;

      if (action === "edit") {
        results[idx] = { ...cur, editing: true };
        renderResults();
        const ta = resultsEl.querySelector<HTMLTextAreaElement>(`textarea[data-role='edit-area'][data-plat='${platform}']`);
        ta?.focus();
        return;
      }
      if (action === "save-edit") {
        const ta = resultsEl.querySelector<HTMLTextAreaElement>(`textarea[data-role='edit-area'][data-plat='${platform}']`);
        const newText = ta?.value ?? cur.text;
        results[idx] = { ...cur, editing: false, text: newText };
        renderResults();
        ctx.showToast("Edits saved", "success");
        return;
      }
      if (action === "cancel-edit") {
        results[idx] = { ...cur, editing: false };
        renderResults();
        return;
      }
      if (action === "copy") {
        void copyToClipboard(cur.text);
        return;
      }
      if (action === "regen") {
        void regenerateOne(platform);
        return;
      }
      if (action === "post") {
        ctx.showToast(
          `Posting to ${PLATFORM_META[platform].label} is a stub — connect TKOS's ${PLATFORM_META[platform].label} OAuth in Settings to enable.`,
          "info",
        );
        return;
      }
    };

    // ── Wiring ────────────────────────────────────────────────────────────

    platformsEl.addEventListener("click", onPlatformClick);
    dropEl.addEventListener("click", onDropClick);
    dropEl.addEventListener("keydown", onDropKey);
    dropEl.addEventListener("dragover", onDragOver);
    dropEl.addEventListener("dragleave", onDragLeave);
    dropEl.addEventListener("drop", onDrop);
    fileInput.addEventListener("change", onFileChange);
    removeBtn.addEventListener("click", onRemoveImage);
    descEl.addEventListener("input", onDescInput);
    quickEl.addEventListener("click", onQuickClick);
    generateBtn.addEventListener("click", onGenerateClick);
    resultsEl.addEventListener("click", onResultsClick);

    // ── Boot ──────────────────────────────────────────────────────────────
    renderPlatformChips();
    renderImagePreview();
    renderResults();
    void fetchRecent();

    // ── Cleanup ───────────────────────────────────────────────────────────
    return () => {
      ac.abort();
      platformsEl.removeEventListener("click", onPlatformClick);
      dropEl.removeEventListener("click", onDropClick);
      dropEl.removeEventListener("keydown", onDropKey);
      dropEl.removeEventListener("dragover", onDragOver);
      dropEl.removeEventListener("dragleave", onDragLeave);
      dropEl.removeEventListener("drop", onDrop);
      fileInput.removeEventListener("change", onFileChange);
      removeBtn.removeEventListener("click", onRemoveImage);
      descEl.removeEventListener("input", onDescInput);
      quickEl.removeEventListener("click", onQuickClick);
      generateBtn.removeEventListener("click", onGenerateClick);
      resultsEl.removeEventListener("click", onResultsClick);
      if (imageObjectUrl) {
        URL.revokeObjectURL(imageObjectUrl);
        imageObjectUrl = null;
      }
    };
  },
};

registerPage(socialPage);

export default socialPage;
